import {
  Image,
  ImageWrapper,
  Information,
  InformationTopWrapper,
  InformationBottomWrapper,
  ProductCode,
  ProductCodeWrapper,
  ProductName,
  ProductPricePerUnit,
  Wrapper,
  ProductErpCatalog,
} from './ProductCard.styles.ts'
import ProductPlaceholderImage from '@/core/assets/images/product-placeholder.svg'
import { formatCurrency } from '@/core/utils/formatCurrency.ts'
import { FC, MouseEvent, ReactNode, useState } from 'react'
import { Product } from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'
import { useTheme } from '@mui/material'
import { useGetMeQuery } from '@/core/api/graphql/forshawGraphqlApi.generated.ts'
import { useGetAccountBranch } from '@/core/hooks/useGetAccountBranch.ts'
import ProductQuantityModal from './ProductQuantityModal.tsx'

interface Props {
  product: Product
  actions?: ReactNode
  backgroundColor?: string
  priceColor?: string
  onClick?: (product: Product) => void
  hoverEnabled?: boolean
}

const ProductCard: FC<Props> = ({
  product,
  actions,
  backgroundColor,
  priceColor,
  onClick,
  hoverEnabled,
}) => {
  const [imageError, setImageError] = useState(false)
  const [quantityModalAnchor, setQuantityModalAnchor] =
    useState<HTMLElement | null>(null)

  const onImageError = () => {
    setImageError(true)
  }

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation()

    onClick?.(product)
  }

  const handlePriceClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setQuantityModalAnchor(event.currentTarget)
  }

  const handleCloseModal = () => {
    setQuantityModalAnchor(null)
  }

  const theme = useTheme()

  const { data: { me } = {} } = useGetMeQuery()

  const accountFromUser = (me as any)?.account
  const { account } = useGetAccountBranch()

  const hasQuantities = Boolean(
    (product.caseQuantity && product.caseQuantity > 0) ||
      (product.palletQuantity && product.palletQuantity > 0),
  )

  return (
    <Wrapper
      backgroundColor={backgroundColor ?? theme.palette.custom.gray4}
      hoverEnabled={hoverEnabled}
      onClick={handleClick}
    >
      <ImageWrapper>
        {product.photo && !imageError ? (
          <Image src={product.photo} onError={onImageError} />
        ) : (
          <ProductPlaceholderImage />
        )}
      </ImageWrapper>
      <Information>
        <InformationTopWrapper>
          <ProductName unavailable={product.unavailable}>
            {product.name}
          </ProductName>
          {actions}
        </InformationTopWrapper>

        <InformationBottomWrapper>
          <ProductCodeWrapper>
            <ProductCode>{product.code}</ProductCode>

            {(account || accountFromUser)?.erpCatalogs?.length > 1 &&
              product.erpCatalogName && (
                <ProductErpCatalog>
                  • {product.erpCatalogName}
                </ProductErpCatalog>
              )}
          </ProductCodeWrapper>

          <ProductPricePerUnit
            color={priceColor ?? theme.palette.custom.blue1}
            clickable={hasQuantities}
            onClick={hasQuantities ? handlePriceClick : undefined}
          >
            {product.unit
              ? `${formatCurrency(product.price)} / ${product.unit}`
              : formatCurrency(product.price)}
          </ProductPricePerUnit>
        </InformationBottomWrapper>
      </Information>
      <ProductQuantityModal
        anchorEl={quantityModalAnchor}
        onClose={handleCloseModal}
        caseQuantity={product.caseQuantity}
        palletQuantity={product.palletQuantity}
      />
    </Wrapper>
  )
}

export default ProductCard
